import React, { useContext, useMemo } from "react";
import { Form, useField } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import arrayMutators from "final-form-arrays";
import { IntlContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { ACTIVE_SERVICE_CATEGORIES, ACTIVE_SERVICE_CATEGORY_KEYS } from "constants/categories";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import Button from "ui/Button";
import { useRegisterStepper } from "../common/RegisterStepper";
import type { RegisterDraft } from "../types";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";

interface Props {
    client: RegisterDraft | undefined;
    setClient: (client: RegisterDraft) => void;
}

type FieldArrayFields = {
    value?: string[];
    push: (value: string) => void;
    remove: (index: number) => void;
};

const ServiceSelection: React.FC<Props> = ({ client, setClient }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();

    const categories = useMemo(
        () =>
            ACTIVE_SERVICE_CATEGORY_KEYS.map((categoryKey) => {
                const config = ACTIVE_SERVICE_CATEGORIES[categoryKey];

                return {
                    key: config.category.toString(),
                    label: intl.formatMessage(`category.${categoryKey.toLowerCase()}`),
                    Icon: config.Icon,
                    subcategories: config.subcategories.map(({ key }) => ({
                        value: key,
                        label: intl.formatMessage(`our_services.label.${key}`),
                    })),
                };
            }),
        [intl],
    );

    const titlePart1 = intl.formatMessage("authentication.register.client_flow.services.title.part1");
    const titleHighlight = intl.formatMessage("authentication.register.client_flow.services.title.highlight");

    const onSubmit = async (values: Record<string, unknown>) => {
        try {
            setClient(Object.assign({}, client, values) as RegisterDraft);
            trackRegisterStepComplete({
                registerType: "client",
                stepKey: "services",
                stepIndex: 1,
                stepLabel: intl.formatMessage("authentication.register.client_flow.services.label"),
            });
            stepper.goTo("need");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={client}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, values, submitting, form }) => (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            {(() => {
                                const draftValues = values as RegisterDraft;
                                const selectedCategoryKey = Array.isArray(draftValues?.services)
                                    ? (draftValues.services?.[0] as string | undefined)
                                    : undefined;
                                const selectedCategory = categories.find((c) => c.key === selectedCategoryKey);
                                const showingSubcategories = !!selectedCategoryKey && !!selectedCategory;

                                if (!showingSubcategories) {
                                    return (
                                        <>
                                            <p className="h1 font-black">
                                                {titlePart1}{" "}
                                                <span className="block text-primary">{titleHighlight}</span>
                                            </p>
                                            <p className="h4">
                                                {intl.formatMessage("authentication.register.client_flow.services.subtitle.select_category")}
                                            </p>

                                            <div className="register__group-fields">
                                                <div className="register__grid">
                                                    <FieldArray name="services">
                                                        {({ fields }: { fields: FieldArrayFields }) => {
                                                            const selectedServices: string[] = fields.value ?? [];

                                                            return (
                                                                <div className="grid gap-4 sm:grid-cols-2">
                                                                    {categories.map(({ key, label, Icon }) => {
                                                                        const checked = selectedServices.includes(key);
                                                                        const isHighlighted = checked;

                                                                        return (
                                                                            <div key={key} className="w-full">
                                                                                <button
                                                                                    type="button"
                                                                                    className={[
                                                                                        "flex w-full items-center justify-between gap-4 rounded-[22px] border bg-background px-6 py-5 transition hover:bg-accent/40",
                                                                                        isHighlighted
                                                                                            ? "border-primary bg-primary/5"
                                                                                            : "border-input",
                                                                                    ].join(" ")}
                                                                                    role="radio"
                                                                                    aria-checked={checked}
                                                                                    onClick={() => {
                                                                                        for (let i = selectedServices.length - 1; i >= 0; i--) {
                                                                                            fields.remove(i);
                                                                                        }
                                                                                        fields.push(key);
                                                                                        form.change("subcategories", []);
                                                                                    }}
                                                                                >
                                                                                    <div className="flex min-w-0 flex-1 items-center gap-4">
                                                                                        <div
                                                                                            className={[
                                                                                                "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-colors",
                                                                                                isHighlighted
                                                                                                    ? "bg-primary/10 text-primary"
                                                                                                    : "bg-muted/70 text-muted-foreground/70",
                                                                                            ].join(" ")}
                                                                                        >
                                                                                            <Icon className="h-5 w-5" />
                                                                                        </div>
                                                                                        <div
                                                                                            className={[
                                                                                                "text-base font-black tracking-tight",
                                                                                                isHighlighted ? "text-primary" : "",
                                                                                            ].join(" ")}
                                                                                        >
                                                                                            {label}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div
                                                                                        className={[
                                                                                            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition",
                                                                                            checked
                                                                                                ? "bg-primary text-white opacity-100"
                                                                                                : "opacity-0",
                                                                                        ].join(" ")}
                                                                                        aria-hidden="true"
                                                                                    >
                                                                                        <Check className="h-4 w-4" />
                                                                                    </div>
                                                                                </button>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        }}
                                                    </FieldArray>
                                                </div>
                                            </div>

                                            <div className="text-center field__feedback error">
                                                <ShowErrorMessage />
                                            </div>
                                        </>
                                    );
                                }

                                return (
                                    <>
                                        <p className="h1 font-black">
                                            {titlePart1}{" "}
                                            <span className="block text-primary">{titleHighlight}</span>
                                        </p>
                                        <p className="h4">
                                            {intl.formatMessage("authentication.register.client_flow.services.subtitle.select_subcategory", {
                                                category: selectedCategory.label,
                                            })} :
                                            <span className="block text-xs italic text-muted-foreground">{intl.formatMessage("authentication.register.client_flow.services.subtitle.select_subcategory.notice")}</span>
                                        </p>

                                        <div className="register__group-fields">
                                            <div className="register__grid">
                                                <FieldArray name="subcategories">
                                                    {({ fields }: { fields: FieldArrayFields }) => {
                                                        const selectedSubs = fields.value ?? [];

                                                        return (
                                                            <div role="radiogroup" className="grid gap-4">
                                                                {selectedCategory.subcategories.map((sub) => {
                                                                    const subChecked = selectedSubs.includes(sub.value);

                                                                    return (
                                                                        <button
                                                                            key={sub.value}
                                                                            type="button"
                                                                            className={[
                                                                                "flex w-full cursor-pointer items-center justify-between rounded-[20px] border px-6 py-5 text-sm font-semibold transition hover:bg-accent/30",
                                                                                subChecked
                                                                                    ? "border-primary bg-background"
                                                                                    : "border-input bg-background",
                                                                            ].join(" ")}
                                                                            role="radio"
                                                                            aria-checked={subChecked}
                                                                            onClick={() => {
                                                                                for (let i = selectedSubs.length - 1; i >= 0; i--) {
                                                                                    fields.remove(i);
                                                                                }
                                                                                fields.push(sub.value);
                                                                            }}
                                                                        >
                                                                            <span className="font-black text-foreground">
                                                                                {sub.label}
                                                                            </span>
                                                                            <span
                                                                                className={[
                                                                                    "inline-flex h-5 w-5 items-center justify-center rounded-full transition",
                                                                                    subChecked
                                                                                        ? "bg-primary text-white opacity-100"
                                                                                        : "opacity-0",
                                                                                ].join(" ")}
                                                                                aria-hidden="true"
                                                                            >
                                                                                <Check className="h-3.5 w-3.5" />
                                                                            </span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    }}
                                                </FieldArray>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between margin-top--6">
                                            <Button
                                                type="button"
                                                className="btn inline-flex items-center justify-center gap-3 rounded-full px-10 py-4 font-black text-muted-foreground/70"
                                                onClick={() => {
                                                    form.change("services", []);
                                                    form.change("subcategories", []);
                                                }}
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                                {intl.formatMessage("generic.step.previous")}
                                            </Button>

                                            <Button
                                                className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full font-black px-10 py-4"
                                                type="submit"
                                                disabled={
                                                    submitting ||
                                                    !Array.isArray(draftValues?.subcategories) ||
                                                    (draftValues?.subcategories?.length ?? 0) === 0
                                                }
                                            >
                                                {intl.formatMessage("generic.step.next")}
                                                <ChevronRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </form>
                )}
            />
        </React.Fragment>
    );
};

const ShowErrorMessage = () => {
    return useField("services").meta.submitError?.props?.error.message;
};

export default ServiceSelection;
